import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Bell, Check, CheckCheck, Inbox } from "lucide-react";

const POLL_MS = 30_000;

const TYPE_STYLE = {
  survey: { tag: "Sondage", bg: "bg-plum-100 text-plum-700" },
  quiz: { tag: "Quiz", bg: "bg-sun-100 text-sun-700" },
  feedback_reply: { tag: "Réponse mairie", bg: "bg-mint-100 text-mint-600" },
  voucher: { tag: "Bon gagné", bg: "bg-pink-100 text-pink-600" },
  mention: { tag: "Mention", bg: "bg-pink-100 text-pink-600" },
  carpool: { tag: "Covoiturage", bg: "bg-lavande-100 text-lavande-700" },
  project: { tag: "Projet", bg: "bg-sun-100 text-sun-700" },
  system: { tag: "Info", bg: "bg-cream-100 text-plum-700" },
};

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso).getTime();
  const diff = Math.max(0, Date.now() - d) / 1000;
  if (diff < 60) return "à l'instant";
  if (diff < 3600) return `il y a ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `il y a ${Math.floor(diff / 3600)} h`;
  return `il y a ${Math.floor(diff / 86400)} j`;
}

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const navigate = useNavigate();
  const popRef = useRef(null);

  const fetchCount = async () => {
    try {
      const r = await api.get("/notifications/unread-count");
      setUnread(r.data.unread || 0);
    } catch { /* noop */ }
  };
  const fetchList = async () => {
    try {
      const r = await api.get("/notifications");
      setItems(r.data || []);
    } catch { /* noop */ }
  };

  useEffect(() => {
    fetchCount();
    const t = setInterval(fetchCount, POLL_MS);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (open) fetchList();
  }, [open]);

  useEffect(() => {
    const handler = (e) => {
      if (open && popRef.current && !popRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const markRead = async (n) => {
    setItems((prev) => prev.map((it) => (it.id === n.id ? { ...it, read: true } : it)));
    setUnread((u) => Math.max(0, u - (n.read ? 0 : 1)));
    try { await api.post(`/notifications/${n.id}/read`); } catch { /* noop */ }
  };

  const onClick = async (n) => {
    if (!n.read) markRead(n);
    setOpen(false);
    if (n.href) navigate(n.href);
  };

  const markAll = async () => {
    try {
      await api.post("/notifications/read-all");
      setUnread(0);
      setItems((prev) => prev.map((it) => ({ ...it, read: true })));
    } catch { /* noop */ }
  };

  return (
    <div className="relative" ref={popRef}>
      <button
        data-testid="notification-bell"
        onClick={() => setOpen((v) => !v)}
        className="relative w-10 h-10 rounded-2xl bg-cream-50 hover:bg-cream-100 text-plum-700 grid place-items-center transition-colors border border-cream-200"
        aria-label="Notifications"
      >
        <Bell size={16} strokeWidth={2.2} />
        {unread > 0 && (
          <span
            data-testid="notification-badge"
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-pink-500 text-white text-[10px] font-bold grid place-items-center shadow-md border-2 border-white"
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="notification-panel"
          className="absolute right-0 mt-2 w-[340px] sm:w-[380px] max-h-[80vh] bg-white rounded-2xl shadow-2xl border border-cream-200 overflow-hidden z-50 animate-fade-up"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-cream-100 bg-cream-50/50">
            <div>
              <h3 className="font-display font-bold text-plum-900 text-base">Notifications</h3>
              <div className="text-[11px] text-plum-700/60">
                {unread > 0 ? `${unread} non lue${unread > 1 ? "s" : ""}` : "Tout est à jour"}
              </div>
            </div>
            {unread > 0 && (
              <button
                onClick={markAll}
                className="text-[11px] font-semibold text-plum-700 hover:text-plum-800 flex items-center gap-1"
                data-testid="notification-mark-all-read"
              >
                <CheckCheck size={12} /> Tout marquer lu
              </button>
            )}
          </div>

          <div className="overflow-y-auto max-h-[60vh] divide-y divide-cream-100">
            {items.length === 0 && (
              <div className="px-6 py-12 text-center">
                <Inbox size={28} className="mx-auto text-plum-700/30 mb-2" />
                <div className="text-[13px] font-semibold text-plum-700/60">Aucune notification</div>
                <p className="text-[11px] text-plum-700/40 mt-1">
                  Vous serez averti dès qu&apos;un sondage, quiz ou bon est disponible.
                </p>
              </div>
            )}
            {items.map((n) => {
              const style = TYPE_STYLE[n.type] || TYPE_STYLE.system;
              return (
                <button
                  key={n.id}
                  onClick={() => onClick(n)}
                  data-testid={`notification-item-${n.id}`}
                  className={`w-full text-left px-4 py-3 flex gap-3 items-start hover:bg-cream-50 transition-colors ${
                    !n.read ? "bg-plum-50/50" : ""
                  }`}
                >
                  <div className={`w-8 h-8 rounded-xl ${style.bg} grid place-items-center shrink-0 text-sm`}>
                    {n.icon || "•"}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 mb-0.5">
                      <span className={`phenix-pill ${style.bg} text-[9px] py-0`}>{style.tag}</span>
                      <span className="text-[10px] text-plum-700/50">{timeAgo(n.created_at)}</span>
                      {!n.read && <span className="ml-auto w-2 h-2 rounded-full bg-pink-500" />}
                    </div>
                    <h4 className="font-display font-bold text-[13px] text-plum-900 leading-tight line-clamp-2">
                      {n.title}
                    </h4>
                    {n.body && (
                      <p className="text-[11px] text-plum-700/70 mt-0.5 line-clamp-2 leading-snug">{n.body}</p>
                    )}
                  </div>
                  {n.read && <Check size={11} className="text-plum-700/30 shrink-0 mt-1" />}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
